export interface LivestreamTimelineSnapshot {
  _id: string;
  timestamp: string;
  concurrentViewers: number;
  likeCount: number;
}

export interface LivestreamTimelineById {
  actualStartTime?: string;
  releaseTime?: string;
  youtubeDuration?: number;
}

export interface PreparedLivestreamTimelineChartData {
  hasTimelineData: boolean;
  displayStartTime?: string;
  displayDuration: number;
  minuteSnapshots: LivestreamTimelineSnapshot[];
}

export function prepareLivestreamTimelineChartData(input: {
  timelineSnapshots: LivestreamTimelineSnapshot[];
  timelineTotalDuration: number;
  livestreamById: LivestreamTimelineById;
}): PreparedLivestreamTimelineChartData {
  const { timelineSnapshots, timelineTotalDuration, livestreamById } = input;
  const hasTimelineData =
    timelineTotalDuration > 0 && timelineSnapshots.length > 0;

  if (!hasTimelineData) {
    return {
      hasTimelineData: false,
      displayDuration: timelineTotalDuration,
      minuteSnapshots: [],
    };
  }

  const earliestSnapshotTime = Math.min(
    ...timelineSnapshots.map((s) => new Date(s.timestamp).getTime()),
  );
  const displayStartTime = new Date(
    earliestSnapshotTime - 60 * 1000,
  ).toISOString();

  const startTimeMs = new Date(displayStartTime).getTime();
  const latestSnapshotTime = Math.max(
    ...timelineSnapshots.map((s) => new Date(s.timestamp).getTime()),
  );
  const oneMinuteAfterLast = latestSnapshotTime + 60 * 1000;
  const displayDuration = (oneMinuteAfterLast - startTimeMs) / 1000;

  const minuteMap = new Map<
    number,
    { maxViewers: number; maxLikes: number; timestamp: string; _id: string }
  >();

  for (const snapshot of timelineSnapshots) {
    const snapshotTimeMs = new Date(snapshot.timestamp).getTime();
    const offsetSeconds = Math.floor((snapshotTimeMs - startTimeMs) / 1000);
    if (offsetSeconds < 0) continue;

    const minuteIndex = Math.floor(offsetSeconds / 60);
    const existing = minuteMap.get(minuteIndex);
    if (!existing || snapshot.concurrentViewers > existing.maxViewers) {
      minuteMap.set(minuteIndex, {
        maxViewers: snapshot.concurrentViewers,
        maxLikes: snapshot.likeCount ?? 0,
        timestamp: snapshot.timestamp,
        _id: snapshot._id,
      });
    } else if (existing && (snapshot.likeCount ?? 0) > existing.maxLikes) {
      existing.maxLikes = snapshot.likeCount ?? 0;
    }
  }

  const minuteSnapshots = Array.from(minuteMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, data]) => ({
      _id: data._id,
      timestamp: data.timestamp,
      concurrentViewers: data.maxViewers,
      likeCount: data.maxLikes,
    }));

  return {
    hasTimelineData: true,
    displayStartTime,
    displayDuration,
    minuteSnapshots,
  };
}

export interface LivestreamChartPoint {
  offsetSeconds: number;
  viewers: number;
  likes: number;
}

export function buildLivestreamChartPoints(input: {
  snapshots: LivestreamTimelineSnapshot[];
  actualStartTime?: string;
  totalDuration: number;
}): LivestreamChartPoint[] {
  const { snapshots, actualStartTime, totalDuration } = input;
  if (!snapshots.length || totalDuration <= 0) return [];

  const startTime = actualStartTime
    ? new Date(actualStartTime).getTime()
    : new Date(snapshots[0].timestamp).getTime();

  return snapshots
    .map((snapshot) => {
      const snapshotTime = new Date(snapshot.timestamp).getTime();
      const offsetSeconds = Math.floor((snapshotTime - startTime) / 1000);
      return {
        offsetSeconds,
        viewers: snapshot.concurrentViewers,
        likes: snapshot.likeCount,
      };
    })
    .filter(
      (point) =>
        point.offsetSeconds >= 0 && point.offsetSeconds <= totalDuration,
    );
}
